import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  Alert, 
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  CircularProgress,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import { 
  FileText, 
  Filter, 
  ChevronRight,
  Printer,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { format } from 'date-fns';

const VehicleInspectionReviewer = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [printingChecklist, setPrintingChecklist] = useState(false);
  const [printingSafetyLetter, setPrintingSafetyLetter] = useState(false);

  useEffect(() => {
    fetchInspections();
  }, [selectedMonth, selectedYear, statusFilter]);

  const fetchInspections = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const inspectionData = await apiService.getVehicleInspectionsByMonth(
        selectedMonth,
        selectedYear,
        statusFilter
      );
      
      setData(inspectionData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching inspections:', err);
      setError(`Error fetching inspections: ${err.message}`);
      setLoading(false);
    }
  };

  const handlePrintChecklist = async (user) => {
    if (!user.checklist_id) {
      setError('No checklist found for this user');
      return;
    }

    try {
      setPrintingChecklist(true);
      setError(null);
      
      // Open checklist in new window for printing
      window.open(`/admin/print/checklist/${user.checklist_id}`, '_blank');
      
      setSuccess('Opening checklist for printing...');
      setPrintingChecklist(false);
    } catch (err) {
      console.error('Error printing checklist:', err);
      setError(`Error printing checklist: ${err.message}`);
      setPrintingChecklist(false);
    }
  };

  const handlePrintSafetyLetter = async (user) => {
    if (!user.safety_letter_id) {
      setError('No safety letter found for this month');
      return;
    }

    try {
      setPrintingSafetyLetter(true);
      setError(null);
      
      // Open safety letter signature page in new window for printing
      window.open(`/admin/print/safety-letter/${user.safety_letter_id}/user/${user.id}`, '_blank');
      
      setSuccess('Opening safety letter for printing...');
      setPrintingSafetyLetter(false);
    } catch (err) {
      console.error('Error printing safety letter:', err);
      setError(`Error printing safety letter: ${err.message}`);
      setPrintingSafetyLetter(false);
    }
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 3 }, (_, i) => {
    const year = new Date().getFullYear() - 1 + i;
    return { value: year, label: year.toString() };
  });

  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'not_submitted', label: 'Not Submitted' }
  ];

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Vehicle Inspections & Safety Letters
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Filter Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Filter size={20} style={{ marginRight: 8 }} />
              <Typography variant="subtitle1">Filters</Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {months.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {years.map((year) => (
                    <MenuItem key={year.value} value={year.value}>
                      {year.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Results Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Technician Submissions - {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : data && data.users.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Technician</TableCell>
                      <TableCell>Branch</TableCell>
                      <TableCell align="center">Vehicle Inspection</TableCell>
                      <TableCell align="center">Safety Letter</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {user.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>{user.branch}</TableCell>
                        <TableCell align="center">
                          {user.has_checklist ? (
                            <Chip 
                              icon={<CheckCircle size={16} />}
                              label="Submitted" 
                              color="success" 
                              size="small" 
                            />
                          ) : (
                            <Chip 
                              icon={<XCircle size={16} />}
                              label="Missing" 
                              color="error" 
                              size="small" 
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {user.has_safety_letter_signature ? (
                            <Chip 
                              icon={<CheckCircle size={16} />}
                              label="Signed" 
                              color="success" 
                              size="small" 
                            />
                          ) : (
                            <Chip 
                              icon={<XCircle size={16} />}
                              label="Not Signed" 
                              color="error" 
                              size="small" 
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {user.is_submitted ? (
                            <Chip 
                              label="Complete" 
                              color="success" 
                              size="small" 
                            />
                          ) : (
                            <Chip 
                              label="Incomplete" 
                              color="warning" 
                              size="small" 
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {user.has_checklist && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Printer size={16} />}
                                onClick={() => handlePrintChecklist(user)}
                                disabled={printingChecklist}
                              >
                                Print Inspection
                              </Button>
                            )}
                            {user.has_safety_letter_signature && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Printer size={16} />}
                                onClick={() => handlePrintSafetyLetter(user)}
                                disabled={printingSafetyLetter}
                              >
                                Print Safety Letter
                              </Button>
                            )}
                            {!user.has_checklist && !user.has_safety_letter_signature && (
                              <Typography variant="caption" color="text.secondary">
                                No documents to print
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  {statusFilter === 'submitted' 
                    ? 'No technicians have submitted their inspections and safety letters for this period.'
                    : statusFilter === 'not_submitted'
                    ? 'All technicians have completed their submissions for this period.'
                    : 'No data available for the selected period.'}
                </Typography>
              </Box>
            )}

            {data && data.users.length > 0 && (
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Summary:</strong> {data.users.filter(u => u.is_submitted).length} of {data.users.length} technicians have completed both vehicle inspection and safety letter for {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VehicleInspectionReviewer;

